import { useEffect, useMemo, useState } from 'react'

import { CountdownExpiredContext, CountdownTimerContext } from './context'

import type { Maybe } from '@shared/graphql'

export interface CountdownProviderProps {
  /**
   * React Node Children.
   */
  children?: React.ReactNode
  /**
   * Expiration date of the countdown.
   */
  expireAt?: Maybe<Date>
}

export const CountdownProvider: React.FC<CountdownProviderProps> = ({
  children,
  expireAt,
}) => {
  const [millisecondsLeft, setMillisecondsLeft] = useState(
    expireAt ? expireAt.getTime() - new Date().getTime() : Infinity,
  )

  useEffect(() => {
    if (!expireAt) return

    const interval = setInterval(() => {
      const now = new Date()
      const diff = Math.max(0, expireAt.getTime() - now.getTime())
      setMillisecondsLeft(diff)

      if (diff <= 0) {
        clearInterval(interval)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [expireAt, setMillisecondsLeft])

  const timerValue: number = useMemo(() => {
    return millisecondsLeft
  }, [millisecondsLeft])

  const expiredValue: boolean = useMemo(
    () => millisecondsLeft <= 0 && millisecondsLeft !== Infinity,
    [millisecondsLeft],
  )

  return (
    <CountdownTimerContext.Provider value={timerValue}>
      <CountdownExpiredContext.Provider value={expiredValue}>
        {children}
      </CountdownExpiredContext.Provider>
    </CountdownTimerContext.Provider>
  )
}
